import { contactDeLaBulle, lueurDeLaDate } from '@nextilt/commun';
import type { BulleEnrichie, Decompte, Etat, Feuille } from '../partage/api/types';
import { aujourdhui, joursEntre } from '../partage/datesEnMots';
import { jourDeRangement, rangerDansMonOrdre } from './rangerLaListe';

/*
 * Ce que montrent les marges M, R, F (chantier modes-de-la-carte, modification 03 ; maquette
 * `quatre-themes.html`, `margesExecution`) : rien que la feuille ne porte déjà. Le compte de
 * chaque étape est celui du serveur (`decompte`), l'ordre celui de la Hiérarchie
 * (`ordreHierarchique`), le jour d'une bulle celui de la Liste « Par date » (`jourDeRangement`).
 * L'écran ne fait que ranger et poser.
 */

/** Une étape de « Comment j'avance » : son nom, son état, son « N sur M », barrée si faite. */
export interface EtapeDeLAvancement {
  id: string;
  titre: string;
  etat: Etat;
  decompte: Decompte;
  faite: boolean;
}

/** « Comment j'avance » : chaque étape de la feuille, dans ton ordre, et le compte de la feuille. */
export function avancementDeLaFeuille(feuille: Pick<Feuille, 'enfants' | 'ordreHierarchique' | 'bulle'>): {
  etapes: EtapeDeLAvancement[];
  decompte: Decompte;
} {
  return {
    etapes: rangerDansMonOrdre(feuille.enfants, feuille.ordreHierarchique).map((b) => ({
      id: b.id,
      titre: b.titre,
      etat: b.etat,
      decompte: b.decompte,
      faite: b.etat === 'fait',
    })),
    decompte: feuille.bulle.decompte,
  };
}

/** Toutes les bulles que la feuille connaît : le premier tour, puis celles du dessous. */
export function bullesConnues(feuille: Pick<Feuille, 'enfants' | 'carte'>): BulleEnrichie[] {
  return [...feuille.enfants, ...feuille.carte.dessous];
}

/** Un contact du répertoire : son nom, son numéro, et les bulles qui le portent. */
export interface ContactDuRepertoire {
  cle: string;
  nom: string | null;
  numero: string | null;
  bulles: { id: string; titre: string }[];
}

/**
 * Le répertoire : chaque contact des bulles de la feuille, une fois, avec ses bulles (le même
 * contact : `contactDeLaBulle`, de `commun/`, que Regrouper par contact lit aussi au serveur) ; le numéro est le premier posé. Dans l'ordre où la feuille les
 * donne.
 */
export function repertoireDeLaFeuille(feuille: Pick<Feuille, 'enfants' | 'carte'>): ContactDuRepertoire[] {
  const parCle = new Map<string, ContactDuRepertoire>();
  for (const b of bullesConnues(feuille)) {
    const lu = contactDeLaBulle(b);
    if (!lu) continue;
    const { cle, nom, numero } = lu;
    const contact = parCle.get(cle) ?? { cle, nom, numero, bulles: [] };
    contact.numero ??= numero;
    contact.bulles.push({ id: b.id, titre: b.titre });
    parCle.set(cle, contact);
  }
  return [...parCle.values()];
}

/** Un cran de la frise : une bulle datée, son jour, et où elle se pose (de 0 à 1). */
export interface CranDeLaFrise {
  id: string;
  titre: string;
  jour: string;
  place: number;
  faite: boolean;
  /** Dans les sept jours qui viennent. */
  proche: boolean;
}

/**
 * La frise des dates : aujourd'hui, puis chaque bulle datée d'aujourd'hui ou plus tard (son jour
 * « Par date »), dans l'ordre des jours, posée à proportion de son écart à aujourd'hui.
 */
export function friseDeLaFeuille(
  feuille: Pick<Feuille, 'enfants' | 'carte'>,
  jourDAujourdhui: string = aujourdhui(),
  /**
   * L'horizon, en jours : seules les dates d'ici là se posent, les autres se comptent dans
   * `plusLoin` (retour d'Igor du 2026-10-22, choix 3 : « quand il y a beaucoup de choses ça
   * devient ilisible ») ; `null` : toutes.
   */
  horizon: number | null = null
): { aujourdhui: string; crans: CranDeLaFrise[]; plusLoin: number } {
  const toutes = bullesConnues(feuille)
    .map((b) => ({ b, jour: jourDeRangement(b) }))
    .filter((d): d is { b: BulleEnrichie; jour: string } => d.jour !== null && d.jour >= jourDAujourdhui)
    .sort((x, y) => (x.jour < y.jour ? -1 : x.jour > y.jour ? 1 : 0));
  const datees = horizon === null ? toutes : toutes.filter((d) => joursEntre(jourDAujourdhui, d.jour) <= horizon);
  const loin = Math.max(1, ...datees.map((d) => joursEntre(jourDAujourdhui, d.jour)));
  return {
    aujourdhui: jourDAujourdhui,
    plusLoin: toutes.length - datees.length,
    crans: datees.map(({ b, jour }) => {
      const dans = joursEntre(jourDAujourdhui, jour);
      return { id: b.id, titre: b.titre, jour, place: dans / loin, faite: b.etat === 'fait', proche: lueurDeLaDate(dans) === 'proche' };
    }),
  };
}
